import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import { fromJS } from 'immutable';
import { setSnackbar } from 'flow/actions/app-actions';
import { updateProfile } from 'flow/actions/profile-actions';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { isEmptyNullOrUndefined } from 'flow/utils';
import {
    DefaultDuration,
    DurationState,
    getDefaultDurationId,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import Page from '../Setting/Page';
import Button from '../ui-components/Button';
import InputField from '../ui-components/InputField';
import RadioButton from '../ui-components/RadioButton';
import TimeInput from '../ui-components/TimeInput';

const ProfileSettingsDefaultDuration = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { getProfile } = useProfiles(extensionKey);
    const profile = getProfile(profileKey);

    const currentDuration = profile?.get('defaultDuration');
    const isPauseProfile = profile?.get('pauseProfile');
    const seconds = profile?.get('pauseDuration'); //Pause duration in seconds only defined in pause profiles
    let currentDefaultDuration = currentDuration;
    if (!currentDefaultDuration?.startsWith('1')) {
        //If there is no previous specific duration
        currentDefaultDuration = isPauseProfile ? '1;0;0;0;5' : '1;0;0;1;0'; //Use 5mins or 1h as prefilled start value
    }

    const parts = currentDefaultDuration.split(';');

    const durationState = (() => {
        if (profile) {
            if (profile?.get('pauseProfile')) {
                return 'specific';
            }

            return DurationState[
                getDefaultDurationId(profile?.get('defaultDuration'))
            ].value;
        }
    })();

    const [week, setWeek] = useState(parts[1]);
    const [days, setDays] = useState(parts[2]);
    const [hours, setHours] = useState(parts[3]);
    const [minutes, setMinutes] = useState(parts[4]);
    const [duration, setDuration] = useState(durationState);
    const [defaultSeconds, setDefaultSeconds] = useState();

    const timeUntilWeekday = useRef<HTMLInputElement>(null);
    const weeklyHours = parseInt(parts[3]) < 10 ? '0' + parts[3] : parts[3];
    const weeklyMinutes = parseInt(parts[4]) < 10 ? '0' + parts[4] : parts[4];

    const [untilNextWeekdayAt, setUntilNextWeekdayAt] = useState(
        weeklyHours + ':' + weeklyMinutes,
    );

    useEffect(() => {
        if (profile) {
            setDuration(durationState);
        }
    }, [profile]);

    const handleSelection = (value: string, defaultTime = '') => {
        const defaultDurationString = (() => {
            if (value === DefaultDuration.DEFAULT.value) {
                return '0';
            }
            if (value === DefaultDuration.SPECIFIC.value) {
                return '1;0;0;1;0';
            }
            if (value === DefaultDuration.WEEKDAY.value) {
                return defaultTime || '2;1;8;0';
            }
            if (value === DefaultDuration.MONDAY.value) {
                return defaultTime || '2;0;8;0';
            }
            return null;
        })();

        if (defaultDurationString) {
            const updatedProfile = fromJS({
                key: profileKey,
                defaultDuration: defaultDurationString,
            });
            dispatch(updateProfile(extensionKey, updatedProfile));
        }

        if (value === DefaultDuration.DEFAULT.value) {
            history.push(
                generatePath(routes['settings.profiles.profileKey'], {
                    extensionKey,
                    profileKey,
                }),
            );
        }

        setDuration(value);
    };

    const isValidWeek = (input: string) =>
        !isEmptyNullOrUndefined(input) && /^(0|[0-9]|[0-5][0-2])$/.test(input);

    const isValidDay = (input: string) =>
        !isEmptyNullOrUndefined(input) && /^[0-6]$/.test(input);

    const isValidHour = (input: string) =>
        !isEmptyNullOrUndefined(input) &&
        /^(0|[0-9]|1[0-9]|2[0-3])$/.test(input);

    const isValidMinute = (input: string) => {
        if (isPauseProfile) {
            return (
                !isEmptyNullOrUndefined(input) &&
                /^(0|[0-9]|30|[0-2][0-9])$/.test(input)
            );
        }
        return (
            !isEmptyNullOrUndefined(input) &&
            /^(0|[0-9]|[0-5][0-9])$/.test(input)
        );
    };

    const handleDurationChangeSpecific = () => {
        let newDefaultDuration;
        if (
            isValidWeek(week) &&
            isValidDay(days) &&
            isValidHour(hours) &&
            isValidMinute(minutes)
        ) {
            newDefaultDuration =
                '1;' + week + ';' + days + ';' + hours + ';' + minutes;
        }

        if (
            !isEmptyNullOrUndefined(newDefaultDuration) &&
            isEmptyNullOrUndefined(defaultSeconds)
        ) {
            const update = fromJS({
                key: profileKey,
                defaultDuration: newDefaultDuration,
            });

            try {
                dispatch(updateProfile(extensionKey, update));
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'Settings.Update.Snackbar.Success',
                    }),
                );
            } catch (err) {
                dispatch(
                    setSnackbar({
                        i18nextMessage: 'Settings.Update.Snackbar.Fail',
                    }),
                );
                throw err;
            }
        } else if (
            isEmptyNullOrUndefined(newDefaultDuration) &&
            !isEmptyNullOrUndefined(defaultSeconds)
        ) {
            const update = fromJS({
                key: profileKey,
                pauseDuration: defaultSeconds,
            });
            try {
                dispatch(updateProfile(extensionKey, update));
            } catch (err) {
                throw err;
            }
        } else if (
            !isEmptyNullOrUndefined(newDefaultDuration) &&
            !isEmptyNullOrUndefined(defaultSeconds)
        ) {
            const update = fromJS({
                key: profileKey,
                defaultDuration: newDefaultDuration,
                pauseDuration: defaultSeconds,
            });

            try {
                dispatch(updateProfile(extensionKey, update));
            } catch (err) {
                throw err;
            }
        }
    };

    const handleChangeWeekdayOrMonday = () => {
        const day = duration === 'monday' ? '0' : '1';

        const parts = untilNextWeekdayAt.split(':');
        const hour = parts[0];
        const minute = parts[1];
        const defaultDuration = '2;' + day + ';' + hour + ';' + minute;
        const update = fromJS({
            key: profileKey,
            defaultDuration: defaultDuration,
        });
        try {
            dispatch(updateProfile(extensionKey, update));
            dispatch(
                setSnackbar({
                    i18nextMessage: 'Settings.Update.Snackbar.Success',
                }),
            );
        } catch (err) {
            dispatch(
                setSnackbar({
                    i18nextMessage: 'Settings.Update.Snackbar.Fail',
                }),
            );
            throw err;
        }
    };

    return (
        <Page
            header={i18next.t('settings.profile.default_duration')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey,
                    }),
                )
            }
        >
            <div className="gap-2">
                <RadioButton
                    name="untilFurtherNotice"
                    checked={duration === 'default'}
                    onChange={() => handleSelection('default')}
                >
                    {i18next.t('profilechooser.until_further_notice')}
                </RadioButton>

                <RadioButton
                    name="specificDuration"
                    checked={duration === 'specific'}
                    onChange={() => handleSelection('specific')}
                >
                    {i18next.t('settings.profile.specific')}
                </RadioButton>

                <RadioButton
                    name="nextWeekday"
                    checked={duration === 'weekday'}
                    onChange={() => handleSelection('weekday')}
                >
                    {i18next.t('settings.profile.weekday')}
                </RadioButton>

                <RadioButton
                    name="nextMonday"
                    checked={duration === 'monday'}
                    onChange={() => handleSelection('monday')}
                >
                    {i18next.t('settings.profile.monday')}
                </RadioButton>

                {duration === 'specific' ? (
                    <div className="p-3">
                        <InputField
                            className="mb-3"
                            label={i18next.t('settings.profile.weeks')}
                            type={'text'}
                            defaultValue={week}
                            onChange={(e) => setWeek(e.target?.value)}
                        />
                        <InputField
                            className="mb-3"
                            label={i18next.t('settings.profile.days')}
                            type={'text'}
                            defaultValue={days}
                            onChange={(e) => setDays(e.target?.value)}
                        />
                        <InputField
                            className="mb-3"
                            label={i18next.t('settings.profile.hours')}
                            type={'text'}
                            defaultValue={hours}
                            onChange={(e) => setHours(e.target?.value)}
                        />
                        <InputField
                            className="mb-3"
                            label={i18next.t('settings.profile.minutes')}
                            type={'text'}
                            defaultValue={minutes}
                            onChange={(e) => setMinutes(e.target?.value)}
                        />

                        <Button
                            color="primary"
                            className="py-1"
                            onClick={handleDurationChangeSpecific}
                        >
                            {i18next.t('navigate.save')}
                        </Button>
                    </div>
                ) : null}

                {(duration === 'weekday' || duration === 'monday') && (
                    <div className="gap-3">
                        <TimeInput
                            label={i18next.t('to')}
                            ref={timeUntilWeekday}
                            defaultValue={untilNextWeekdayAt}
                            onChange={(e) =>
                                setUntilNextWeekdayAt(e.target?.value)
                            }
                            className="p-2"
                        />
                        <Button
                            color="primary"
                            className="py-1"
                            onClick={handleChangeWeekdayOrMonday}
                        >
                            {i18next.t('navigate.save')}
                        </Button>
                    </div>
                )}
            </div>
        </Page>
    );
};

export default ProfileSettingsDefaultDuration;
